// pdf-viewer.component.ts
import {
    Component, ElementRef, ViewChild, AfterViewInit, OnDestroy, Input, 
    ViewEncapsulation, OnInit, EventEmitter, Output, OnChanges,
    SimpleChanges
} from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Subscription } from 'rxjs';

import { UrlService } from '@core/services/url/url.service';
import { AnnotationsService } from '@core/services/annotations/annotations.service';
import { FileSignatureManagerService } from '@core/services/files/file-signature-manager.service';


import * as Objects from '@core/objects';
import * as pdfModel from '@core/models/xfdf-annotator.model';
import { ViewerCapabilities } from '@core/models/annotator';


@Component({
    selector: 'core-pdf-annotations-viewer',
    templateUrl: './pdf-viewer.component.html',
    styleUrls: [
        '../../../../../node_modules/@twonas/pdfjs-killer-library/dist-lib/pdfjs-killer-library.css',
        './pdf-viewer.component.scss'],
    encapsulation: ViewEncapsulation.ShadowDom
})
export class PdfViewerComponent implements AfterViewInit, OnInit, OnDestroy, OnChanges {

    public readonly STATUS_LOADING = 0;
    public readonly STATUS_DONE = 1;
    public readonly STATUS_NOT_FOUND = 2;
    public readonly STATUS_ERROR = 3;


    @ViewChild('pdfAnnotatorViewer') container!: ElementRef;


    @Input() file: Objects.AmsFile;
    @Input() user: Objects.User = null;
    @Input() viewerUser: Objects.User = null;
    @Input() annotationsAllowed: boolean;
    @Input() urlAnnotationsPrefix: string;
    @Input() urlAnnotationsLoadPrefix: string;
    @Input() context: string = "default";
    @Output() saved = new EventEmitter<boolean>(); 
    @Output() refreshSignaturesNeeded = new EventEmitter<void>();

    public annotations: string;

    public pageStatus: number = this.STATUS_LOADING;
    public annotationsStatus: number = this.STATUS_LOADING;

    private retryDelay = 500; //  half second to get attempts of load pdf
    private viewerCapabilities: ViewerCapabilities;

    // Use refs instead of state to avoid re-renders during init
    private viewer: any;
    private pageHeights: Map<number, number> = new Map();
    private annotationsRef = { value: '' }; // Stores fetched XFDF, not in state
    private suppressedAnnotationIdsRef = new Set<string>(); // Pre-suppress synthetic modify events
    private onAnnotationChangeRef = { current: null as ((type: string, xfdf: string) => void) | null };
    private annotationsFetchedRef = { value: false };
    // Resolves once the annotations GET has settled (done|not_found|error), regardless of
    // how it compares in timing to the PDF load - lets initAnnotations wait on it instead
    // of racing a status flag
    private annotationsFetchPromise: Promise<void> | null = null;
    // Lets teardown force-settle a pending annotationsFetchPromise so an initAnnotations
    // that's awaiting it wakes up and bails via isActiveLoad, instead of hanging forever
    // on a promise whose subscription just got unsubscribed
    private annotationsFetchResolve: (() => void) | null = null;

    private subs: Subscription = new Subscription();
    
    /**
     * Tracks the currently requested PDF URL
     * Used to cancel old async loadPDF (presigned or legacy) calls when user rapidly clicks different files
     * Prevents race conditions where old file data is loaded into a newly-initialized viewer
     */
    private currentLoadingUrl: string = '';

    /** Set once ngOnDestroy runs, so in-flight async chains can bail before touching this.viewer */
    private destroyed = false;

    constructor(
        public urlSrv: UrlService,
        private http: HttpClient,
        private annotationsSrv: AnnotationsService,
        private signatureManager: FileSignatureManagerService) { }

    ngAfterViewInit() {

        if (this.file) {
            this.startPdfProcess();
        }
    }

    ngOnInit(): void {
        // build the url to get the file preview pdf

    }

    ngOnChanges(changes: SimpleChanges): void {
        const fileChange = changes['file'];

        if (fileChange && !fileChange.firstChange) {
            const previousFile: Objects.AmsFile = fileChange.previousValue;
            const currentFile: Objects.AmsFile = fileChange.currentValue;
            const isSameFile = !!previousFile && !!currentFile && previousFile.id === currentFile.id;

            // Same file already loaded (e.g. only its signature was refreshed): skip the
            // reload entirely unless annotationsAllowed actually changed the capabilities
            if (isSameFile && this.pageStatus === this.STATUS_DONE && !this.capabilitiesChanged()) {
                return;
            }

            // Different file, not yet loaded, or capabilities changed: tear down and reload
            this.resetViewerState();
            this.startPdfProcess();
        }
    }

    /**
     * Viewer capabilities are derived solely from annotationsAllowed
     * @returns true if the capabilities computed from the current annotationsAllowed
     * input differ from the ones the mounted viewer was created with
     */
    private capabilitiesChanged(): boolean {
        const newCapabilities = this.annotationsAllowed ? pdfModel.ANNOTATOR_SCENARIOS.REVIEWER : pdfModel.ANNOTATOR_SCENARIOS.READ_ONLY;
        return newCapabilities !== this.viewerCapabilities;
    }

    /**
     * Tears down the current viewer and per-file state before loading a new file.
     * Also invalidates any in-flight load (loadPdfLegacy/loadPdfPresigned or their
     * initAnnotations/extractPageHeights continuations) via currentLoadingUrl, so a
     * stale async chain from the previous file can't call methods on the old/new viewer.
     */
    private resetViewerState(): void {
        this.currentLoadingUrl = '';

        this.viewer?.unmount();
        this.viewer = undefined;

        this.pageStatus = this.STATUS_LOADING;
        this.annotationsStatus = this.STATUS_LOADING;
        this.pageHeights.clear();
        this.annotationsRef.value = '';
        this.annotationsFetchedRef.value = false;
        this.annotationsFetchResolve?.(); // wake any initAnnotations still awaiting the old fetch
        this.annotationsFetchPromise = null;
        this.annotationsFetchResolve = null;
        this.suppressedAnnotationIdsRef.clear();

        this.subs.unsubscribe();
        this.subs = new Subscription();
    }

    /**
     * True while `url` is still the active load, the component isn't destroyed, and a
     * viewer is mounted. Use before touching `this.viewer` from an async continuation.
     */
    private isActiveLoad(url: string): boolean {
        return !this.destroyed && this.currentLoadingUrl === url && !!this.viewer;
    }

    private startPdfProcess() {
        if (!this.file) return;

        // Step A: Fetch annotations
        this.fetchAnnotationsParallel();

        // Step B: Route to the correct PDF loading logic
        if (this.isNewAmsFile(this.file)) {

            // --- NEW FLOW ---
            if (this.signatureManager.isSignatureExpired(this.file)) {

                this.refreshSignaturesNeeded.emit();
                return;
            }
            const publicApiUrl = this.buildPdfPreviewApiUrl();
            this.loadPdfPresigned(publicApiUrl);

        } else {

            // --- LEGACY FLOW ---
            const legacyUrl = this.urlSrv.build('/files/' + this.file.id + '/pdf');
            this.loadPdfLegacy(legacyUrl);
        }

        // Set viewer capabilities based on annotationsAllowed input
        this.viewerCapabilities = this.annotationsAllowed ? pdfModel.ANNOTATOR_SCENARIOS.REVIEWER : pdfModel.ANNOTATOR_SCENARIOS.READ_ONLY; 

    }

    /**
     * Builds the public API URL for fetching the PDF preview of the current file
     * Includes signature and redirect=false to request JSON response instead of a 302 redirect
     * @returns the url for the current file pdf preview
     */
    private buildPdfPreviewApiUrl(): string {
        const basePath = `/pub/p/${this.file.uuid}/pdf`;
        const params = new URLSearchParams();

        params.append('signature', this.file.access.signature);
        params.append('redirect', 'false'); // Request JSON response, not a 302 redirect

        return this.urlSrv.buildFiles(`${basePath}?${params.toString()}`);
    }

    /**
     * Step A: Eagerly fetch XFDF annotations in parallel
     * Stores result in ref (not state) to avoid re-renders
     */
    private fetchAnnotationsParallel(): void {
        if (!this.annotationsSrv || this.annotationsFetchedRef.value || this.context == 'version') // when context version,  there is no notes
            return;
        
        this.annotationsFetchedRef.value = true;

        this.annotationsFetchPromise = new Promise<void>((resolve) => {
            this.annotationsFetchResolve = resolve;
            this.subs.add(
                this.annotationsSrv.find(this.file, this.urlAnnotationsLoadPrefix ?? this.urlAnnotationsPrefix).subscribe({
                    next: (annotationsXfdf: string) => {
                        if (annotationsXfdf != null) {
                            this.annotationsRef.value = annotationsXfdf;
                            this.annotationsStatus = this.STATUS_DONE;
                        }
                        resolve();
                    },
                    error: (err: HttpErrorResponse) => {
                        if (err.status === 404) {
                            this.annotationsStatus = this.STATUS_NOT_FOUND;
                        } else {
                            console.debug(" xfdf annotations load error.", err);
                            this.annotationsStatus = this.STATUS_ERROR;
                        }
                        resolve();
                    }
                })
            );
        });
    }

    private async initAnnotations(url: string) {
        if (this.context === 'version') return; // No notes in version context

        if (!this.isActiveLoad(url)) return; // superseded or destroyed - viewer is no longer ours

        if (this.pageStatus !== this.STATUS_DONE) {
            return;
        }

        // The annotations GET runs in parallel with the PDF load and can still be in-flight
        // here (PDF often loads faster). Wait for it to settle instead of racing a status flag.
        if (this.annotationsFetchPromise) {
            await this.annotationsFetchPromise;
        }

        if (!this.isActiveLoad(url)) return; // superseded or destroyed while awaiting annotations

        // STATUS_DONE (found), STATUS_NOT_FOUND (no annotations yet, the common case for a
        // fresh file) and STATUS_ERROR all mean the fetch has settled; only STATUS_LOADING
        // (no annotationsSrv/promise available) should still block us here
        if (this.annotationsStatus === this.STATUS_LOADING) {
            return;
        }

        // Extract page heights after PDF and annotations are loaded. The library measures
        // page sizes progressively after loadFile/loadFileURL resolve, so wait until every
        // page referenced by the XFDF has a height - otherwise importFromXfdf silently drops
        // the annotations on pages not measured yet
        const requiredPages = this.annotationsStatus === this.STATUS_DONE
            ? this.getRequiredPages(this.annotationsRef.value)
            : new Set<number>();
        await this.extractPageHeights(url, requiredPages);

        if (!this.isActiveLoad(url)) return; // superseded or destroyed mid-extraction

        // Import annotations using ref value (already fetched in parallel), only if some were found
        if (this.annotationsStatus === this.STATUS_DONE) {
            this.importXfdf();
        }

        // Subscribe to onChange only if annotations are allowed
        if (this.annotationsAllowed) {
            this.subscribeToAnnotationChanges();
        }
    }

    /**
     * Subscribe to onChange events: add | modify | delete
     * Includes loop suppression for setCustomData synthetic events
     * Detects reply annotations (which have parentAnnotationId) separately from regular annotations
     */
    private subscribeToAnnotationChanges(): void {
        this.subs.add(
            this.viewer.manager.onChange((event: any) => {
                const annotationId = event.annotation?.id;
                const isReply = !!event.parentAnnotationId; // Detect reply by presence of parentAnnotationId

                // a) Loop suppression: skip if this ID was pre-suppressed
                if (this.suppressedAnnotationIdsRef.has(annotationId)) {
                    this.suppressedAnnotationIdsRef.delete(annotationId);
                    return;
                }


                // b) Tag with TwonaUserId (only on add/modify, NOT on delete, only if not already set)
                if (event.type !== 'delete' && this.user && (event.type === 'add' || event.type === 'modify')) {
                    // For replies, we handle custom data differently on export, 
                    // so we skip setCustomData here to avoid triggering unwanted modify events
                    if (!isReply) {
                        // Regular annotation: tag it now
                        const wrappers = this.viewer.manager.getAnnotationsList();
                        const wrapper = wrappers.find((w: any) => w.annotation.id === annotationId);
                        if (wrapper && wrapper.getCustomData?.('TwonaUserId') !== this.user.id) {
                            // Pre-suppress the next modify that setCustomData will trigger
                            this.suppressedAnnotationIdsRef.add(annotationId);

                            wrapper.setCustomData('TwonaUserId', this.user.id);
                        }
                    }
                }

                // c) Export and save for all event types (add, modify, delete)
                // Pass event object to handle replies differently
                this.exportAndSaveAnnotation(event.type, annotationId, event);
            })
        );
    }

    /**
     * Export only the affected annotation (not all) and save to backend
     * Handles both regular annotations and reply annotations
     */
    private exportAndSaveAnnotation(eventType: string, annotationId: string, event: any): void {
        try {
            let annotationToExport: any;
            let xfdfSanitized: string;
            const isReply = !!event.parentAnnotationId;

            // Handle reply annotations (which come with partial XFDF content)
            if (isReply) {
                
                // Build complete XFDF with custom data injected inside the text node
                const customDataJson = JSON.stringify({ 'TwonaUserId': this.user.id });
                const escapedCustomData = this.escapeXml(customDataJson);
                
                // Extract the text node from event.xfdf and inject trn-custom-data inside it
                const textNodeWithCustomData = event.xfdf.replace(
                    /(<text[^>]*>)/,
                    `$1\n            <trn-custom-data bytes="${escapedCustomData}" />`
                );
                
                const xfdfToExport = 
                    '<?xml version="1.0" encoding="UTF-8"?>\n' +
                    '<xfdf xmlns="http://ns.adobe.com/xfdf/" xml:space="preserve">\n' +
                    '    <annots>\n' +
                    textNodeWithCustomData + '\n' +
                    '    </annots>\n' +
                    '</xfdf>';

                // Sanitize whitespace/newlines
                xfdfSanitized = xfdfToExport
                    .replace(/(<trn-custom-data[^>]*?)\s*\/>/g, '$1/>')
                    .replace(/\s+/g, ' ')
                    .replace(/>\s+</g, '><')
                    .replace(/\n/g, '');


                this.saveAnnotations({ 
                    action: eventType, 
                    content: xfdfSanitized,
                    legacy: false 
                });
                return;
            }

            // Handle regular annotations and deletes
            if (eventType === 'delete') {
                // For delete, annotation may not be in manager anymore, but event has the data
                annotationToExport = event?.annotation;
                
                if (!annotationToExport) {
                    console.warn(`Could not find annotation data for deletion ${annotationId}`);
                    return;
                }
            } else {
                // For add/modify, fetch the current annotation from the manager
                const updatedWrapper = this.viewer.manager
                    .getAnnotationsList()
                    .find((w: any) => w.id === annotationId);

                if (!updatedWrapper) {
                    console.warn(`Could not find annotation ${annotationId} for export`);
                    return;
                }

                // Get the raw annotation to export (includes freshly-set custom data)
                annotationToExport = updatedWrapper?.raw ?? updatedWrapper;
            }

            // The page may not have been measured when heights were extracted (e.g. a note
            // added on a late page right after load): refresh from the viewer before exporting
            const annotationPage = annotationToExport?.page;
            if (annotationPage && !this.pageHeights.has(annotationPage)) {
                this.refreshPageHeights();
            }

            // Export only this one annotation
            const xfdfExport = PdfAnnotator.XfdfConverter.exportToXfdf(
                [annotationToExport],
                this.pageHeights
            );

            // Sanitize whitespace/newlines
            xfdfSanitized = xfdfExport
                .replace(/(<trn-custom-data[^>]*?)\s*\/>/g, '$1/>')
                .replace(/\s+/g, ' ')
                .replace(/>\s+</g, '><')
                .replace(/\n/g, '');


            // Call backend via service
            this.saveAnnotations({ 
                action: eventType, 
                content: xfdfSanitized, 
                legacy: false 
            });
        } catch (error) {
            console.error(`Error exporting annotation ${annotationId}:`, error);
        }
    }

    /**
     * Collects the 1-indexed pages referenced by the annotations of an XFDF document
     * @param xfdf the XFDF content
     * @returns the set of pages (empty if the XFDF can't be parsed)
     */
    private getRequiredPages(xfdf: string): Set<number> {
        const pages = new Set<number>();
        if (!xfdf || xfdf.trim() === '') return pages;

        try {
            const doc = new DOMParser().parseFromString(xfdf, 'application/xml');
            if (doc.getElementsByTagName('parsererror').length > 0) return pages;

            const annots = doc.getElementsByTagName('annots')[0];
            if (!annots) return pages;

            Array.from(annots.children).forEach((el: Element) => {
                const page = parseInt(el.getAttribute('page') || '0', 10);
                if (!isNaN(page)) {
                    pages.add(page + 1); // XFDF is 0-indexed, pageHeights is 1-indexed
                }
            });
        } catch (error) {
            console.warn('Could not read pages from XFDF:', error);
        }

        return pages;
    }

    /**
     * Merges the page sizes currently measured by the viewer into pageHeights
     * @returns the number of pages reported by the viewer
     */
    private refreshPageHeights(): number {
        const pageSizes = this.viewer?.getPageSizes?.();
        if (!pageSizes || !Array.isArray(pageSizes)) return 0;

        pageSizes.forEach((pageInfo: any) => {
            if (pageInfo.page && pageInfo.heightPt) {
                this.pageHeights.set(pageInfo.page, pageInfo.heightPt);
            }
        });

        return pageSizes.length;
    }

    /**
     * Waits until the viewer has measured every page in requiredPages (or at least one page
     * when none are required). The library measures pages progressively after the load
     * resolves, so an early read may only contain the first page.
     * Stops early if the measured count stops growing (XFDF referencing a page the PDF
     * doesn't have) and gives up after a timeout, filling missing pages with a fallback height.
     */
    private async extractPageHeights(url: string, requiredPages: Set<number>): Promise<void> {
        const pollInterval = 150;
        const timeout = 15000;
        const stableChecksToStop = 3;

        const startedAt = Date.now();
        let lastCount = -1;
        let stableChecks = 0;

        this.pageHeights.clear();

        while (Date.now() - startedAt < timeout) {
            if (!this.isActiveLoad(url)) {
                return; // superseded or destroyed - no point retrying against a stale viewer
            }

            try {
                const count = this.refreshPageHeights();
                const allRequiredMeasured = Array.from(requiredPages).every(p => this.pageHeights.has(p));

                if (count > 0 && allRequiredMeasured) {
                    return;
                }

                // Count no longer growing: the viewer finished measuring and some required
                // page doesn't exist in this PDF
                if (count > 0 && count === lastCount) {
                    stableChecks++;
                    if (stableChecks >= stableChecksToStop) break;
                } else {
                    stableChecks = 0;
                }
                lastCount = count;
            } catch (error) {
                console.warn('Error reading page sizes:', error);
            }

            await this.delay(pollInterval);
        }

        if (!this.isActiveLoad(url)) return;

        // Fill missing required pages with the most common measured height (or A4)
        // so their annotations are still displayed instead of dropped
        const missingPages = Array.from(requiredPages).filter(p => !this.pageHeights.has(p));
        const fallbackHeight = this.getMostCommonPageHeight() ?? 842;

        if (this.pageHeights.size === 0) {
            this.pageHeights.set(1, fallbackHeight);
        }

        if (missingPages.length > 0) {
            console.warn('Page heights not available for pages, using fallback height', fallbackHeight, missingPages);
            missingPages.forEach(p => this.pageHeights.set(p, fallbackHeight));
        }
    }

    private getMostCommonPageHeight(): number | null {
        const counts = new Map<number, number>();
        this.pageHeights.forEach(h => counts.set(h, (counts.get(h) ?? 0) + 1));

        let best: number | null = null;
        let bestCount = 0;
        counts.forEach((count, h) => {
            if (count > bestCount) {
                best = h;
                bestCount = count;
            }
        });
        return best;
    }

    importXfdf(): void {
        try {
            // Use ref value (fetched in parallel)
            const xfdf = this.annotationsRef.value;
            
            if (!xfdf || xfdf.trim() === '') {
                return;
            }

            const imported = PdfAnnotator.XfdfConverter.importFromXfdf(
                xfdf,
                this.pageHeights
            );

            if (!imported || !Array.isArray(imported)) {
                return;
            }

            // importFromXfdf skips annotations on pages without a known height: make it visible
            const expected = this.getRequiredPages(xfdf);
            const missingPages = Array.from(expected).filter(p => !this.pageHeights.has(p));
            if (missingPages.length > 0) {
                console.warn('XFDF annotations dropped, no page height for pages:', missingPages);
            }

            imported.forEach((ann: any) =>
                this.viewer.manager.addAnnotation(ann)
            );
        } catch (error) {
            console.error('Error importing XFDF annotations:', error);
            if (error instanceof TypeError && error.message.includes('reading')) {
                console.error('Missing required parameter. Ensure pageHeights is correct:', this.pageHeights);
            }
        }
    }

    ngOnDestroy() {
        this.destroyed = true;
        this.annotationsFetchResolve?.(); // wake any initAnnotations still awaiting the fetch
        this.viewer?.unmount();
        this.subs.unsubscribe();
    }

    protected initialyzeViwer() {
        this.viewer = PdfAnnotator.mount(
            this.container.nativeElement,
            {
                capabilities: this.viewerCapabilities
            }
        );
        // console.log('Viewer initialized with capabilities:', this.viewerCapabilities, ' for user:', this.user);
        if (this.viewerCapabilities.canAnnotate) {
            let authorName = this.user.name == this.user.email ? this.user.email.replace('@', '_at_') : this.user.name + ' (' + this.user.email.replace('@', '_at_') + ')';
            this.viewer.manager.setCurrentUser(authorName);
            
        }
    }

    protected delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    protected async checkPDFStatus(url: string): Promise<number> {
        const headers = new HttpHeaders({ 'Content-Type': 'application/pdf' });
        try {
            const response = await firstValueFrom(this.http.head(url, { headers, observe: 'response', withCredentials: true }));
            return response.status;
        } catch (error: any) {
            console.debug(' pdf preview error:', error);
            return error?.status;
        }
    }

    /**
     * 
     * @param legacyPdfUrl the url to download the pdf from
     */
    protected async loadPdfLegacy(url: string): Promise<void> {
        // Register this as the currently requested file
        this.currentLoadingUrl = url;
        const status = await this.checkPDFStatus(url);

        // GUARD: User clicked another file while checking PDF status
        if (this.currentLoadingUrl !== url) {
            return;
        }

        if (status === 200) {
            this.pageStatus = this.STATUS_DONE;
            this.initialyzeViwer();
            
            // NEXT TICK: Allow Angular to update Shadow DOM and mount viewer
            await new Promise(resolve => setTimeout(resolve, 0));

            // GUARD: Check before expensive fetch
            if (this.currentLoadingUrl !== url) {
                return;
            }

            var buf = await fetch(url, { credentials: 'include' }).then(r => r.arrayBuffer());
            
            // GUARD: Final check before calling library methods
            if (this.currentLoadingUrl !== url) {
                return;
            }

            try {
                await this.viewer.loadFile(buf, this.file.name);
                await this.initAnnotations(url);
            } catch (error) {
                console.warn('The viewer was re-routed or interrupted safely:', error);
            }
        } else if (status === 425) {
            await this.delay(this.retryDelay);
            await this.loadPdfLegacy(url);
        } else {
            console.log('PDF preview not available ', status);
            this.pageStatus = this.STATUS_NOT_FOUND;
        }
    }


    /**
     * Requests the JSON form (redirect=false) to get the presigned S3 url directly,
     * then hands that url to loadFileURL so the library's own credentials-aware XHR
     * fetches the bytes. Cross-origin signed urls like this one are fetched without
     * credentials by loadFileURL, avoiding the GET/HEAD redirect-method mismatch entirely.
     * @param publicPdfUrl the url from the public api to get the pdf from
     */
    protected async loadPdfPresigned(publicPdfUrl: string): Promise<void> {
        // Register this as the currently requested file
        this.currentLoadingUrl = publicPdfUrl;
        try {
            const response = await firstValueFrom(
                this.http.get<{ url: string; expires_at: string }>(publicPdfUrl, { observe: 'response' })
            );

            // GUARD: User clicked another file while checking PDF status
            if (this.currentLoadingUrl !== publicPdfUrl) {
                return;
            }

            if (response.status === 200) {
                this.pageStatus = this.STATUS_DONE;
                this.initialyzeViwer();

                // NEXT TICK: Allow Angular to update Shadow DOM and mount viewer
                await new Promise(resolve => setTimeout(resolve, 0));

                // GUARD: Check before calling library methods
                if (this.currentLoadingUrl !== publicPdfUrl) {
                    return;
                }

                try {
                    await this.viewer.loadFileURL(response.body!.url, this.file.name);
                    await this.initAnnotations(publicPdfUrl);
                } catch (error) {
                    console.warn('The viewer was re-routed or interrupted safely:', error);
                }
            } else if (response.status === 202) {
                // PDF is still generating, wait and retry
                await this.delay(this.retryDelay);
                await this.loadPdfPresigned(publicPdfUrl);
            } else {
                console.log('PDF preview not available ', response.status);
                this.pageStatus = this.STATUS_NOT_FOUND;
            }
        } catch (error: any) {
            if (this.currentLoadingUrl !== publicPdfUrl) return;

            if (error.status === 202) {
                // In case Angular throws an HttpErrorResponse on 202
                await this.delay(this.retryDelay);
                await this.loadPdfPresigned(publicPdfUrl);
            } else if (error.status === 403) {
                // Signature was technically valid locally but rejected by the server
                this.refreshSignaturesNeeded.emit();
            } else {
                console.log('PDF preview not available ', error.status);
                this.pageStatus = this.STATUS_NOT_FOUND;
            }
        }
    }

    protected saveAnnotations(changes: pdfModel.PdfXfdfAnnotationChanges): void {
        this.annotationsSrv.saveXfdf(this.file, changes, this.urlAnnotationsPrefix, this.context).subscribe({
            next: (data: any) => {
                //  emit saved EVENT to refresh notes counter in parent component
                this.saved.emit(true);
            },
            error: (err: HttpErrorResponse) => {
                console.debug(" annotations error saving ", err);
            }
        });
    }

    protected escapeXml(str: string): string {
        return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&apos;');
    }


    /**
     * Type guard to check if the file is the new AmsFile format
     */
    private isNewAmsFile(file: any): boolean {
        return !!(file && file.access && file.access.signature);
    }
}